import { useCallback, useEffect, useState } from 'react';

import { Achievement, AchievementContext } from '../achievements/types';
import type { GalaxyState } from '../components/SpaceCivMode';
import {
  Position,
  Building,
  BuildingCategory,
  BuildMenuState,
  BiomeConfig,
  BeltItem,
  AssemblerInventory,
  ChestInventory,
  ResearchId,
  ItemType,
  BeltType,
} from '../constants';
import type {
  UpgradeAssemblerResult,
  QuickBeltPlacementResult,
  QuickBeltPreviewState,
} from '../GameEngine';
import type {
  PlayerInventory,
  RejectedItem,
  PowerDemandByBuildingType,
  PowerSnapshot,
  SaveData,
  ResourceNodeMenuState,
  MineResourceResult,
  CollectBuildingInventoryResult,
  ItemProductionHistoryEntry,
} from '../GameState';
import { useGameState } from '../GameState';
import { HandCraftingQueueEntry } from '../HandCrafting';
import { PrestigeUpgradeId, PrestigeUpgradeState } from '../PrestigeSystem';
import { ResearchState } from '../ResearchSystem';
import {
  DEFAULT_RENDERER_MODE,
  RendererMode,
  isRendererMode,
} from '../types/Renderer';
import { isMobileDevice } from '../utils/device';
import { useWorldGeneration } from '../WorldGeneration';

import { UseBuildingsReturn } from './useBuildings';
import { DebugState } from './useDebugState';
import { useToast } from './useToast';

const RENDERER_STORAGE_KEY = 'automationgame.rendererMode';

// Note: UI state interfaces removed - now handled in AppContent

// World hook return type
export interface UseWorldReturn {
  // Loading state
  isLoading: boolean;
  // Game state
  playerPos: Position;
  gameMap: Record<string, string>;
  buildingLayer: Record<string, string>;
  beltContentLayer: Record<string, BeltItem[]>;
  assemblerInventoryLayer: Record<string, AssemblerInventory>;
  chestInventoryLayer: Record<string, ChestInventory>;
  rejectedItemsLayer: Record<string, RejectedItem>;
  minerTimerLayer: Record<string, number>;
  minerOutputBufferLayer: Record<string, number>;
  playerInventory: PlayerInventory;
  prestigePoints: number;
  prestigeCount: number;
  cameraPos: Position;
  currentBiome: string;
  playerFacing: 'up' | 'down' | 'left' | 'right';
  zoomLevel: number;
  tickSpeedMultiplier: number;
  researchState: ResearchState;
  researchMenuOpen: boolean;
  powerProduction: number;
  powerDemand: number;
  powerSatisfaction: number;
  powerHistory: PowerSnapshot[];
  powerDemandByBuildingType: PowerDemandByBuildingType;
  productionMenuOpen: boolean;
  itemProductionHistory: ItemProductionHistoryEntry[];
  currentTick: number;
  handCraftingQueue: HandCraftingQueueEntry[];
  prestigeMenuOpen: boolean;
  prestigeUpgradeState: PrestigeUpgradeState;
  spaceGalaxyState: GalaxyState;
  quickBeltPreview: QuickBeltPreviewState | null;

  // Build menu state
  buildMenuOpen: BuildMenuState;
  selectedCategory: BuildingCategory | null;
  selectedBuilding: Building | null;

  // UI menu states
  helpMenuOpen: boolean;
  saveMenuOpen: boolean;
  leftPanelOpen: boolean;
  debugMenuOpen: boolean;
  achievementMenuOpen: boolean;
  buildingStatusMenuTarget: { x: number; y: number } | null;
  resourceNodeMenu: ResourceNodeMenuState | null;
  hasEnabledDebug: boolean;
  achievements: Achievement[];
  rendererMode: RendererMode;

  // World generation functions
  getTile: (
    x: number,
    y: number,
    gameMap: Record<string, string>,
    buildingLayer?: Record<string, string>
  ) => string | undefined;
  preGenerateArea: (centerX: number, centerY: number, radius?: number) => void;
  getBiome: (x: number, y: number) => BiomeConfig;
  // Game state actions
  collectResource: (x: number, y: number) => void;
  collectBuildingInventory: (
    position: Position,
    itemType?: ItemType
  ) => CollectBuildingInventoryResult;
  openResourceNodeMenu: (position: Position) => void;
  closeResourceNodeMenu: () => void;
  mineResourceNode: (position: Position) => MineResourceResult;
  updateBiome: (biomeName: string) => void;
  movePlayer: (newX: number, newY: number) => void;
  updateTile: (x: number, y: number, character: string) => void;
  placeBuilding: (x: number, y: number, building: Building) => boolean;
  deconstructBuilding: (x: number, y: number) => boolean;
  upgradeAssemblerToFactory: (position: Position) => UpgradeAssemblerResult;
  getTargetPosition: (building?: Building) => Position;
  getBuildingTargetLocation: (building: Building) => Position;
  addItemToInventory: (itemType: ItemType, amount: number) => number;
  queueHandCrafting: (itemType: ItemType, amount: number) => boolean;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  setTickSpeedMultiplier: (multiplier: number) => void;
  cycleTickSpeedMultiplier: () => number;
  saveGame: () => boolean;
  loadGameFromSaveData: (saveData: SaveData) => void;
  resetGame: () => void;
  launchRocket: () => void;
  forcePrestige: (count: number) => void;
  startQuickBeltPlacement: (beltType: BeltType, position: Position) => void;
  extendQuickBeltPlacement: (position: Position) => void;
  finalizeQuickBeltPreview: () => void;
  cancelQuickBeltPlacement: () => void;
  confirmQuickBeltPlacement: () => QuickBeltPlacementResult;
  updatePlayerInventory: (
    updater: (inventory: PlayerInventory) => PlayerInventory
  ) => void;
  setHasEnabledDebug: (enabled: boolean) => void;
  setAssemblerInventoryLayer: React.Dispatch<
    React.SetStateAction<Record<string, AssemblerInventory>>
  >;
  setChestInventoryLayer: React.Dispatch<
    React.SetStateAction<Record<string, ChestInventory>>
  >;
  setRejectedItemsLayer: React.Dispatch<
    React.SetStateAction<Record<string, RejectedItem>>
  >;
  setBeltContentLayer: React.Dispatch<
    React.SetStateAction<Record<string, BeltItem[]>>
  >;
  setBuildingLayer: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  setMinerTimerLayer: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  setMinerOutputBufferLayer: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  setSpaceGalaxyState: React.Dispatch<React.SetStateAction<GalaxyState>>;
  getAchievements: () => Achievement[];
  triggerAchievementCheck: (context: AchievementContext) => void;
  setBuildMenuState: (updates: {
    buildMenuOpen?: BuildMenuState;
    selectedCategory?: BuildingCategory | null;
    selectedBuilding?: Building | null;
  }) => void;
  setBuildingStatusMenuTarget: (
    target: { x: number; y: number } | null
  ) => void;
  openBuildingStatusMenu: (position: { x: number; y: number }) => void;
  closeBuildingStatusMenu: () => void;
  unlockResearch: (researchId: ResearchId) => void;
  forceUnlockResearch: (researchId: ResearchId) => void;
  isResearchUnlocked: (researchId: ResearchId) => boolean;
  getUnlockedResearchIds: () => ResearchId[];
  selectResearch: (researchId: ResearchId | null) => void;
  setResearchMenuOpen: (open: boolean) => void;
  setProductionMenuOpen: (open: boolean) => void;
  setPrestigeMenuOpen: (open: boolean) => void;
  selectPrestigeUpgrade: (upgradeId: PrestigeUpgradeId | null) => void;
  purchasePrestigeUpgrade: (upgradeId: PrestigeUpgradeId) => boolean;
  resetResearch: (researchId: ResearchId) => void;
  resetAllResearch: () => void;
  setRendererMode: (mode: RendererMode) => void;
  toggleRendererMode: () => void;
}

// Hook that combines world generation with game state
export const useWorld = (
  _buildingState: UseBuildingsReturn | null,
  debugState: DebugState
): UseWorldReturn => {
  // Toast management
  const { showError } = useToast();

  // Game state management
  const gameState = useGameState(debugState);
  const [rendererMode, setRendererModeState] = useState<RendererMode>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_RENDERER_MODE;
    }

    try {
      const storedValue = window.localStorage.getItem(RENDERER_STORAGE_KEY);
      if (storedValue && isRendererMode(storedValue)) {
        return storedValue;
      }
    } catch {
      // Ignore storage errors (private browsing, etc.)
    }

    if (isMobileDevice()) {
      return 'ascii';
    }

    return DEFAULT_RENDERER_MODE;
  });

  const persistRendererMode = useCallback((mode: RendererMode) => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(RENDERER_STORAGE_KEY, mode);
    } catch {
      // Ignore storage errors
    }
  }, []);

  const setRendererMode = useCallback(
    (mode: RendererMode) => {
      setRendererModeState(prevMode => {
        if (prevMode === mode) {
          return prevMode;
        }

        persistRendererMode(mode);
        return mode;
      });
    },
    [persistRendererMode]
  );

  const toggleRendererMode = useCallback(() => {
    setRendererModeState(prevMode => {
      const nextMode: RendererMode = prevMode === 'ascii' ? 'three' : 'ascii';
      persistRendererMode(nextMode);
      return nextMode;
    });
  }, [persistRendererMode]);

  useEffect(() => {
    persistRendererMode(rendererMode);
  }, [persistRendererMode, rendererMode]);
  const {
    isLoading,
    playerPos,
    gameMap,
    buildingLayer,
    beltContentLayer,
    setBeltContentLayer,
    assemblerInventoryLayer,
    setAssemblerInventoryLayer,
    chestInventoryLayer,
    setChestInventoryLayer,
    rejectedItemsLayer,
    setRejectedItemsLayer,
    minerTimerLayer,
    setMinerTimerLayer,
    minerOutputBufferLayer,
    setMinerOutputBufferLayer,
    prestigePoints,
    prestigeCount,
    cameraPos,
    currentBiome,
    playerFacing,
    zoomLevel: currentZoomLevel,
    researchState: currentResearchState,
    researchMenuOpen: currentResearchMenuOpen,
    productionMenuOpen: currentProductionMenuOpen,
    prestigeMenuOpen: currentPrestigeMenuOpen,
    prestigeUpgradeState: currentPrestigeUpgradeState,
    handCraftingQueue,
    spaceGalaxyState,
    itemProductionHistory,
    currentTick,
    quickBeltPreview,
    setSpaceGalaxyState,
  } = gameState;

  const zoomLevel = currentZoomLevel;
  const researchState = currentResearchState;
  const researchMenuOpen = currentResearchMenuOpen;
  const productionMenuOpen = currentProductionMenuOpen;
  const prestigeMenuOpen = currentPrestigeMenuOpen;
  const prestigeUpgradeState = currentPrestigeUpgradeState;

  // World generation
  const { getTile, preGenerateArea, initializeWorld, getBiome } =
    useWorldGeneration();

  // Initialize world generation
  useEffect(() => {
    initializeWorld();
  }, [initializeWorld]);

  // Note: useKeyboardInput is now called in AppContent where it has access to real UI state

  return {
    // Loading state
    isLoading,
    // Game state
    playerPos,
    gameMap,
    buildingLayer,
    beltContentLayer,
    assemblerInventoryLayer,
    chestInventoryLayer,
    rejectedItemsLayer,
    minerTimerLayer,
    minerOutputBufferLayer,
    prestigePoints,
    prestigeCount,
    cameraPos,
    currentBiome,
    playerFacing,
    zoomLevel,
    tickSpeedMultiplier: gameState.tickSpeedMultiplier,
    researchState,
    researchMenuOpen,
    productionMenuOpen,
    powerProduction: gameState.powerProduction,
    powerDemand: gameState.powerDemand,
    powerSatisfaction: gameState.powerSatisfaction,
    powerHistory: gameState.powerHistory,
    powerDemandByBuildingType: gameState.powerDemandByBuildingType,
    itemProductionHistory,
    currentTick,
    handCraftingQueue,
    // Build menu state
    buildMenuOpen: gameState.buildMenuOpen,
    selectedCategory: gameState.selectedCategory,
    selectedBuilding: gameState.selectedBuilding,
    // UI menu states
    helpMenuOpen: gameState.helpMenuOpen,
    saveMenuOpen: gameState.saveMenuOpen,
    leftPanelOpen: gameState.leftPanelOpen,
    debugMenuOpen: gameState.debugMenuOpen,
    achievementMenuOpen: gameState.achievementMenuOpen,
    hasEnabledDebug: gameState.hasEnabledDebug,
    achievements: gameState.achievements,
    rendererMode,
    buildingStatusMenuTarget: gameState.buildingStatusMenuTarget,
    resourceNodeMenu: gameState.resourceNodeMenu,
    playerInventory: gameState.playerInventory,
    prestigeMenuOpen,
    prestigeUpgradeState,
    spaceGalaxyState,
    quickBeltPreview,
    // World generation functions
    getTile,
    preGenerateArea,
    getBiome,
    // Game state actions
    collectResource: gameState.collectResource,
    collectBuildingInventory: gameState.collectBuildingInventory,
    updateBiome: gameState.updateBiome,
    movePlayer: gameState.movePlayer,
    updateTile: gameState.updateTile,
    placeBuilding: (x: number, y: number, building: Building) =>
      gameState.placeBuilding(
        x,
        y,
        building,
        (tileX, tileY, gameMap) =>
          getTile(tileX, tileY, gameMap, buildingLayer),
        preGenerateArea,
        showError
      ),
    deconstructBuilding: (x: number, y: number) =>
      gameState.deconstructBuilding(x, y, showError),
    upgradeAssemblerToFactory: (position: Position) =>
      gameState.upgradeAssemblerToFactory(position),
    getTargetPosition: gameState.getTargetPosition,
    getBuildingTargetLocation: gameState.getBuildingTargetLocation,
    addItemToInventory: gameState.addItemToInventory,
    queueHandCrafting: gameState.queueHandCrafting,
    zoomIn: gameState.zoomIn,
    zoomOut: gameState.zoomOut,
    resetZoom: gameState.resetZoom,
    setTickSpeedMultiplier: gameState.setTickSpeedMultiplier,
    cycleTickSpeedMultiplier: gameState.cycleTickSpeedMultiplier,
    saveGame: gameState.saveGame,
    loadGameFromSaveData: gameState.loadGameFromSaveData,
    resetGame: gameState.resetGame,
    launchRocket: gameState.launchRocket,
    forcePrestige: gameState.forcePrestige,
    startQuickBeltPlacement: gameState.startQuickBeltPlacement,
    extendQuickBeltPlacement: gameState.extendQuickBeltPlacement,
    finalizeQuickBeltPreview: gameState.finalizeQuickBeltPreview,
    cancelQuickBeltPlacement: gameState.cancelQuickBeltPlacement,
    confirmQuickBeltPlacement: gameState.confirmQuickBeltPlacement,
    updatePlayerInventory: gameState.updatePlayerInventory,
    setHasEnabledDebug: gameState.setHasEnabledDebug,
    setAssemblerInventoryLayer,
    setChestInventoryLayer,
    setRejectedItemsLayer,
    setBeltContentLayer,
    setBuildingLayer: gameState.setBuildingLayer,
    setMinerTimerLayer,
    setMinerOutputBufferLayer,
    setSpaceGalaxyState,
    getAchievements: gameState.getAchievements,
    triggerAchievementCheck: gameState.triggerAchievementCheck,
    setBuildMenuState: gameState.setBuildMenuState,
    setBuildingStatusMenuTarget: gameState.setBuildingStatusMenuTarget,
    openBuildingStatusMenu: gameState.openBuildingStatusMenu,
    closeBuildingStatusMenu: gameState.closeBuildingStatusMenu,
    openResourceNodeMenu: gameState.openResourceNodeMenu,
    closeResourceNodeMenu: gameState.closeResourceNodeMenu,
    mineResourceNode: gameState.mineResourceNode,
    unlockResearch: gameState.unlockResearch,
    forceUnlockResearch: gameState.forceUnlockResearch,
    isResearchUnlocked: gameState.isResearchUnlocked,
    getUnlockedResearchIds: gameState.getUnlockedResearchIds,
    selectResearch: gameState.selectResearch,
    setResearchMenuOpen: gameState.setResearchMenuOpen,
    setProductionMenuOpen: gameState.setProductionMenuOpen,
    setPrestigeMenuOpen: gameState.setPrestigeMenuOpen,
    selectPrestigeUpgrade: gameState.selectPrestigeUpgrade,
    purchasePrestigeUpgrade: gameState.purchasePrestigeUpgrade,
    resetResearch: gameState.resetResearch,
    resetAllResearch: gameState.resetAllResearch,
    setRendererMode,
    toggleRendererMode,
  };
};
